import PropTypes from 'prop-types';
import React, { Fragment, useEffect } from 'react';
import classNames from 'classnames';
import { Record } from 'immutable';

import { track } from '@xanadu/ui-shared/interfaces/woopra';

import Button from '@xanadu/ui-shared/components/widgets/Button';

import IconCheck from '../../../../../images/icons/misc/check.svg';
import IconClose from '../../../../../images/icons/misc/close.svg';
import InPlayIcon from '../../../../../images/betslip/UI_icon_goinginplay.svg';
import ThirdPartyIcon from '../../../../../images/betslip/UI_icon-3rdPartyBet.svg';
import DFIcon from '../../../../../images/betslip/UI_icon-R4Bet.svg';
import PromoIcon from '../../../../../images/betslip/UI_promos.svg';

import ZeroIcon from '../../../../../images/betslip/icon-mbzero.svg';

import style from './OfferButtons.module.scss';

const OfferButtons = ({
  offer,
  aggregationRule,
  isLoggedIn,
  eventInRunning,
  handleSubmit,
  offerIndex,
  confirmed,
  editModal,
  setEditModal,
  acceptNewBetBuilderOdds,
  showBetBuilderOddsAcceptance,
  setConfirmed,
  countdown,
  processingState,
  activeOffer,
  onCancelOffer,
  onCancelUnmatchedOffers,
  isMBZero,
  betData,
  marketStatus,
  cashoutAvailable,
  selectRunner,
  setShowCashout,
  showCashout,
  setEditUnmatched,
  editUnmatched,
  resetEditing,
  unmatchedData,
  handleEdit
}) => {
  const bettingAvailable = !['closed', 'suspended'].includes(marketStatus);
  const isOfferMatched = offer.status === 'matched';
  let showDF = false;
  const { adjustedOdds, back } = betData;
  const disabledSubmitButton = adjustedOdds === '' || back === '' || back === undefined;
  const classForAnalytics = isLoggedIn ? 'place-bet-btn' : '';
  const isSubmitting = processingState === 'submitting';

  const unmatchedUseBonus = activeOffer && offer.unmatched.length >= 1
    ? offer.unmatched.every(unmatched => unmatched.useBonus)
    : false;

  if (aggregationRule !== 'AVG') {
    if (offer.matchedBets.size > 0) {
      offer.matchedBets.forEach((matchedBet) => {
        showDF = matchedBet.adjustments && matchedBet.adjustments.length > 0;
      });
    }
  }

  const handleCashout = () => {
    setShowCashout(true);
    selectRunner(offer.runnerId);
    track('Betslip', 'Clicked cashout button');
  };

  const handleCancel = () => {
    if (!activeOffer) {
      setConfirmed(false);
    }
    if (offer.id && !unmatchedData) {
      onCancelOffer([offer.id]);
    }
    if (unmatchedData) {
      onCancelUnmatchedOffers([unmatchedData]);
    }
  };

  useEffect(() => {
    if (isOfferMatched) {
      setEditUnmatched(false);
      setEditModal(false);
    }
  }, [isOfferMatched]);

  const button = classNames({
    [style.button]: true,
    [style['button--submitting']]: isSubmitting,
  });

  const buttonConfirm = classNames({
    [style.buttonConfirm]: true,
    [style[`button--${processingState}`]]: true,
    [style['buttonConfirm--disabled']]: disabledSubmitButton || countdown > 0,
  });

  const buttonCancel = classNames({
    [style.buttonCancel]: true,
    [style['buttonCancel--disabled']]: isOfferMatched,
  });

  const buttonEdit = classNames({
    [style.buttonEdit]: true,
    [style['buttonEdit--disabled']]: isOfferMatched || offer.useBonus || unmatchedUseBonus,
  });

  const buttonCashout = classNames({
    [style.buttonCashout]: true,
    [style['buttonCashout--disabled']]: !cashoutAvailable || !bettingAvailable || isMBZero
  });

  const actionsLabel = classNames({
    [style.actionsLabel]: true,
    [style['actionsLabel--disabled']]: countdown > 0
  });

  const rowIcon = classNames({
    [style.rowIcon]: true,
    [style['rowIcon--disableEdit']]: countdown > 0,
    [style['rowIcon--disable']]: disabledSubmitButton,
  });

  return (
    <div>
      {activeOffer ? (
        <div className={style.containerIconAndButtons}>
          <div className={style.containerIcons}>
            {offer.keepInPlay ? <InPlayIcon /> : null}
            {offer.thirdPartyClientId ? <ThirdPartyIcon /> : null}
            {showDF ? <DFIcon /> : null}
            {offer.useBonus || unmatchedUseBonus ? <PromoIcon /> : null}
            {isMBZero ? <ZeroIcon /> : null}
          </div>
          {!showCashout && (
            <div className={style.containerOfferButtons}>
              {!editUnmatched && !editModal && (
                <button
                  className={buttonCancel}
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >Delete</button>
              )}
              {(editModal || editUnmatched) && (
                <button
                  className={buttonCancel}
                  onClick={resetEditing}
                  disabled={isSubmitting}
                >Go Back</button>
              )}
              {!editUnmatched && !editModal && (
                <button
                  className={buttonEdit}
                  onClick={handleEdit}
                  disabled={isSubmitting}
                >Edit</button>
              )}
              {!editUnmatched && !editModal && (
                <button
                  className={buttonCashout}
                  onClick={handleCashout}
                  disabled={isSubmitting}
                >Cashout</button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className={style.containerOfferActions}>
          {showBetBuilderOddsAcceptance ? (
            <Fragment>
              <div className={style.secondAction}>
                <span>
                  Your odds have changed
                </span>
                <span>
                  Accept Changes?
                </span>
              </div>
              <div className={style.firstAction}>
                <Button
                  sizeType="wider"
                  buttonType="confirm"
                  onClick={acceptNewBetBuilderOdds}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') acceptNewBetBuilderOdds();
                  }}
                  title="Accept new Bet Builder Odds"
                >
                  Accept
                </Button>
              </div>
            </Fragment>
          ) : (
            <Fragment>
              <div className={style.secondAction}>
                {confirmed && (
                  <Button
                    sizeType="extra-small"
                    buttonType="cancel"
                    disabled={countdown > 0}
                    className={button}
                    style={{ width: '100%' }}
                    onClick={() => handleCancel()}
                    title="Cancel Bet Confirmation"
                    onKeyUp={(e) => {
                      if (e.key === 'Enter') handleCancel();
                    }}
                    dataHook={`betslipcanceloffer-${offerIndex}`}
                  >
                    <IconClose className={rowIcon} />
                    <span className={actionsLabel}>Cancel</span>
                  </Button>
                )}
              </div>
              <div className={style.firstAction}>
                <Button
                  sizeType="extra-small"
                  loading={(processingState === 'submitting' && !eventInRunning)}
                  buttonType="confirm"
                  disabled={countdown > 0 || disabledSubmitButton}
                  className={`${buttonConfirm} ${classForAnalytics}`}
                  onClick={handleSubmit}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') handleSubmit();
                  }}
                  dataHook={`betslipcheckmark-${offerIndex}`}
                  title="Confirm Bet"
                >
                  {eventInRunning && processingState === 'submitting' && countdown > 0 ? (
                    <Fragment>
                      <img
                        alt="loading"
                        src="/assets/images/icons/live-betting-grey.svg"
                        role="presentation"
                      />
                      <span className={actionsLabel}>{`IN-PLAY DELAY... ${countdown}`}</span>
                    </Fragment>
                  ) : (
                    <Fragment>
                      <IconCheck className={rowIcon} />
                      <span className={actionsLabel}>Confirm Bet</span>
                    </Fragment>
                  )}
                </Button>
              </div>
            </Fragment>
          )}
        </div>
      )}
    </div>
  );
};

OfferButtons.propTypes = {
  offer: PropTypes.instanceOf(Record),
  offerIndex: PropTypes.number,
  countdown: PropTypes.number.isRequired,
  aggregationRule: PropTypes.string,
  isLoggedIn: PropTypes.bool,
  editUnmatched: PropTypes.bool.isRequired,
  showCashout: PropTypes.bool.isRequired,
  onCancelOffer: PropTypes.func,
  eventInRunning: PropTypes.bool,
  confirmed: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  onCancelUnmatchedOffers: PropTypes.func,
  setEditModal: PropTypes.func.isRequired,
  showBetBuilderOddsAcceptance: PropTypes.bool,
  acceptNewBetBuilderOdds: PropTypes.func,
  setConfirmed: PropTypes.func.isRequired,
  resetEditing: PropTypes.func,
  handleEdit: PropTypes.func,
  activeOffer: PropTypes.bool.isRequired,
  processingState: PropTypes.string.isRequired,
  unmatchedData: PropTypes.string,
  isMBZero: PropTypes.bool.isRequired,
  betData: PropTypes.instanceOf(Object).isRequired,
  setShowCashout: PropTypes.func.isRequired,
  cashoutAvailable: PropTypes.bool.isRequired,
  editModal: PropTypes.bool.isRequired,
  marketStatus: PropTypes.string,
  selectRunner: PropTypes.func.isRequired,
  setEditUnmatched: PropTypes.func.isRequired,
};

export default OfferButtons;
